import {
  ComponentType,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useDeepCompareEffect from 'react-use/lib/useDeepCompareEffect';

import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useSearchParamsWithValues } from '../../hooks/useSearchParamsWithValues';
import { SearchUtils } from '../../utils';

export interface FilterEditorComponentProps<T> {
  filter: Filter<T>;
  value: T | null;
  onValueChange: (value: T) => void;
  onClose: () => void;
}

export interface FilterViewerComponentProps<T> {
  filter: Filter<T>;
  value: T;
  onValueChange: (value: T) => void;
  onRemove: () => void;
}

export interface Filter<T = any> {
  id: string;
  label: string;
  category?: string;
  hotkey?: string;
  editor: ComponentType<FilterEditorComponentProps<T>>;
  viewer: ComponentType<FilterViewerComponentProps<T>>;
  query: (value: T) => string;
}

export function createFilter<T>(filter: Filter<T>) {
  return filter;
}
interface StoredFilter<T> {
  id: string;
  value: T;
}

export interface ActiveFilter<T> extends Filter<T> {
  value: T;
}

type FilterContext = {
  query: string;
  setQuery: (query: string) => void;
  filters: Filter<any>[];
  activeFilters: ActiveFilter<any>[];
  addFilter: (filter: Filter<any>, value: any) => void;
  removeFilter: (id: string) => void;
  resetFilters: () => void;
  searchField: string;
};

export const FILTER_CONTEXT = createContext<FilterContext>({
  filters: [],
  query: '',
  setQuery: () => {},
  activeFilters: [],
  addFilter: () => {},
  removeFilter: () => {},
  resetFilters: () => {},
  searchField: '',
});

export function FiltersProvider({
  filters = [],
  children,
  storageKey = 'filter-list',
  searchField = 'displayName',
  defaultFilters = [],
}: {
  filters?: Filter<any>[];
  storageKey: string;
  children: ReactNode;
  searchField: string;
  defaultFilters?: Array<{ id: string; value: any }>;
}) {
  const [stateStorage, setStateStorage] = useLocalStorage<StoredFilter<any>[]>(
    `filter-list-${storageKey}`,
    defaultFilters,
  );
  const { searchParams, setSearchParams } = useSearchParamsWithValues();
  // current active filters. initially sourced from local storage, then query params, then default filters
  const [activeFilters, setActiveFilters] = useState<ActiveFilter<any>[]>(
    () => {
      const storedFilters = Object.fromEntries(
        stateStorage.map(f => [f.id, f.value]),
      );
      // first check if there are any filters in the query param that are relevant
      const queryFilters = filters
        .filter(f => searchParams[f.id] !== undefined)
        .map(f => ({
          ...f,
          value:
            typeof searchParams[f.id] === 'string'
              ? (searchParams[f.id] as string).split(',')
              : searchParams[f.id],
        }));

      const filtersFromStorage = filters
        .filter(f => storedFilters[f.id] !== undefined)
        .map(f => ({
          ...f,
          value: storedFilters[f.id],
        }));

      return !queryFilters.length ? filtersFromStorage : queryFilters;
    },
  );

  // Track filter IDs to detect newly added filters
  const knownFilterIdsRef = useRef<Set<string>>(
    new Set(filters.map(f => f.id)),
  );

  // Restore filters that are added dynamically (e.g., label filters loaded async)
  useEffect(() => {
    const currentIds = new Set(filters.map(f => f.id));
    const newFilters = filters.filter(
      f => !knownFilterIdsRef.current.has(f.id),
    );

    if (newFilters.length > 0) {
      const storedFilters = Object.fromEntries(
        stateStorage.map(f => [f.id, f.value]),
      );

      // Prefer URL params over storage
      const filtersFromUrl = newFilters
        .filter(f => searchParams[f.id] !== undefined)
        .map(f => ({
          ...f,
          value:
            typeof searchParams[f.id] === 'string'
              ? (searchParams[f.id] as string).split(',')
              : searchParams[f.id],
        }));

      const filtersFromStorage = newFilters
        .filter(
          f =>
            storedFilters[f.id] !== undefined &&
            searchParams[f.id] === undefined,
        )
        .map(f => ({
          ...f,
          value: storedFilters[f.id],
        }));

      const filtersToRestore = [...filtersFromUrl, ...filtersFromStorage];

      if (filtersToRestore.length > 0) {
        setActiveFilters(current => {
          const existingIds = new Set(current.map(f => f.id));
          const toAdd = filtersToRestore.filter(f => !existingIds.has(f.id));
          return [...current, ...toAdd];
        });
      }
    }

    knownFilterIdsRef.current = currentIds;
  }, [filters, searchParams, stateStorage]);

  const [query, setQuery] = useState(searchParams.query ?? '');

  useDeepCompareEffect(() => {
    // Update state storage only if there are no search params
    if (Object.keys(searchParams).length === 0) {
      setStateStorage(activeFilters.map(f => ({ id: f.id, value: f.value })));
    }

    const params = activeFilters.reduce<Record<string, any>>((acc, f) => {
      return { ...acc, [f.id]: f.value };
    }, {});

    params.query = query;

    setSearchParams(params);
  }, [activeFilters, query]);

  const addFilter = useCallback(
    (filter: Filter<any>, value: any) => {
      setActiveFilters(currentFilters => {
        // try to keep the existing filter if it exists, in the same order
        const existingFilter = currentFilters.find(f => f.id === filter.id);
        if (existingFilter) {
          const updatedFilters = currentFilters.map(f =>
            f.id === filter.id ? { ...f, value } : f,
          );
          setStateStorage(
            updatedFilters.map(f => ({ id: f.id, value: f.value })),
          );
          return updatedFilters;
        }

        // otherwise, add the new filter to the end of the list
        const newFilters = [...currentFilters, { ...filter, value }];
        setStateStorage(newFilters.map(f => ({ id: f.id, value: f.value })));
        return newFilters;
      });
    },
    [setStateStorage],
  );

  const removeFilter = useCallback(
    (id: string) => {
      setActiveFilters(currentFilters => {
        const newFilters = currentFilters.filter(f => f.id !== id);
        setStateStorage(newFilters.map(f => ({ id: f.id, value: f.value })));
        return newFilters;
      });
    },
    [setStateStorage],
  );

  const resetFilters = useCallback(() => {
    setActiveFilters([]);
  }, []);

  const value = useMemo(
    () => ({
      filters,
      activeFilters,
      addFilter,
      removeFilter,
      query,
      setQuery,
      searchField,
      resetFilters,
    }),
    [
      filters,
      activeFilters,
      addFilter,
      removeFilter,
      query,
      setQuery,
      searchField,
    ],
  );

  return (
    <FILTER_CONTEXT.Provider value={value}>{children}</FILTER_CONTEXT.Provider>
  );
}

export function useFilters() {
  const context = useContext(FILTER_CONTEXT);

  if (!context)
    throw new Error('useFilterList must be used within a FilterListProvider');

  const {
    searchField,
    activeFilters,
    query,
    resetFilters,
    setQuery,
    addFilter,
    removeFilter,
  } = context;

  const filter = useMemo(() => {
    const parts = activeFilters
      .map(f => f.query(f.value))
      .filter(Boolean)
      .map(f => `(${f})`);

    if (query !== '') {
      const escaped = SearchUtils.escapeQuery(query);
      parts.push(`(${searchField}:${escaped}*)`);
    }

    return parts.join(' AND ');
  }, [activeFilters, query, searchField]);

  const isDefaultFilterState = useMemo(() => {
    return activeFilters.length === 0 && !query;
  }, [activeFilters, query]);

  // we return something that looks like the old useListFilter hook
  return {
    searchQuery: query,
    filterString: filter,
    activeFilters,
    resetFilters,
    isDefaultFilterState,

    // Exported to allow usage of FiltersProvider independently of the UI.
    setQuery,
    addFilter,
    removeFilter,
  };
}
